<template>
  <div class="recharge-dialog-root">
    <el-dialog
      :title="$t('recharge.title')"
      :visible.sync="rechargeDialogVisible"
      width="420px"
      top="0"
      append-to-body
      custom-class="recharge-dialog-vc recharge-float"
      @closed="onRechargeDialogClosed"
    >
      <el-form label-position="top" class="recharge-float__form">
        <el-form-item :label="$t('recharge.amount')">
          <div class="recharge-float__presets">
            <el-button
              v-for="n in rechargePresets"
              :key="n"
              size="small"
              :type="rechargeAmount === n ? 'primary' : 'default'"
              plain
              @click="rechargeAmount = n"
            >
              ¥{{ n }}
            </el-button>
          </div>
          <el-input-number
            v-model="rechargeAmount"
            :min="0.01"
            :max="50000"
            :precision="2"
            :step="10"
            class="recharge-float__amount"
            controls-position="right"
          />
          <p v-if="matchedGiftTip" class="recharge-float__gift">{{ matchedGiftTip }}</p>
          <p v-if="matchedCashbackTip" class="recharge-float__gift">{{ matchedCashbackTip }}</p>
        </el-form-item>
        <el-form-item :label="$t('recharge.payType')">
          <el-select v-model="rechargePayType" class="recharge-float__full">
            <el-option label="支付宝" value="alipay" />
            <el-option label="微信支付" value="wxpay" />
          </el-select>
        </el-form-item>
        <el-button type="primary" class="recharge-float__submit" :loading="rechargePayLoading" @click="onRechargePay">
          {{ $t('recharge.submit') }}
        </el-button>
      </el-form>
    </el-dialog>

    <el-dialog
      :visible.sync="rechargeQrVisible"
      :title="$t('recharge.scanTitle')"
      width="360px"
      top="0"
      append-to-body
      center
      custom-class="recharge-dialog-vc recharge-float"
    >
      <div class="recharge-float__qr">
        <img v-if="rechargeQrDataUrl" class="recharge-float__qrImg" :src="rechargeQrDataUrl" alt="qrcode" />
        <div class="recharge-float__qrTip">{{ $t('recharge.scanTip') }}</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="onRechargeQrDone">{{ $t('recharge.paidGoResult') }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :visible.sync="rechargeRawVisible"
      :title="$t('recharge.payInfo')"
      width="520px"
      top="0"
      append-to-body
      custom-class="recharge-dialog-vc recharge-float"
    >
      <pre class="recharge-float__raw">{{ rechargeRawPayInfo }}</pre>
    </el-dialog>

    <el-dialog
      :title="$t('recharge.resultTitle')"
      :visible.sync="rechargeResultVisible"
      width="440px"
      top="0"
      append-to-body
      custom-class="recharge-dialog-vc recharge-float"
      @closed="onRechargeResultClosed"
    >
      <p class="recharge-float__desc">{{ $t('recharge.resultSub') }}</p>
      <div v-if="rechargeResultLoading" class="recharge-float__state">{{ $t('recharge.polling') }}</div>
      <div v-else-if="rechargeResultError" class="recharge-float__state recharge-float__state--err">
        {{ rechargeResultError }}
      </div>
      <div v-else-if="rechargeResultOrder" class="recharge-float__detail">
        <div class="recharge-float__row">
          <span>{{ $t('recharge.orderNo') }}</span>
          <code>{{ rechargeResultOrder.out_trade_no }}</code>
        </div>
        <div class="recharge-float__row">
          <span>{{ $t('recharge.amount') }}</span>
          <strong>{{ rechargeFormatMoney(rechargeResultOrder.amount) }}</strong>
        </div>
        <div class="recharge-float__row">
          <span>{{ $t('recharge.status') }}</span>
          <el-tag :type="rechargeStatusType(rechargeResultOrder.status)" size="small">{{
            rechargeStatusText(rechargeResultOrder.status)
          }}</el-tag>
        </div>
        <div v-if="rechargeResultOrder.trade_no" class="recharge-float__row">
          <span>{{ $t('recharge.platformNo') }}</span>
          <code>{{ rechargeResultOrder.trade_no }}</code>
        </div>
      </div>
      <el-button
        v-if="!rechargeResultLoading && rechargeResultOrder && rechargeResultOrder.status === 'pending'"
        type="primary"
        class="recharge-float__submit"
        @click="pollRechargeResultOnce(false)"
      >
        {{ $t('recharge.refresh') }}
      </el-button>
      <el-button class="recharge-float__submit" plain @click="onRechargeResultCloseBtn">
        {{ $t('recharge.backHome') }}
      </el-button>
    </el-dialog>

    <PayQrDanmaku :active="rechargeQrVisible" />
  </div>
</template>

<script>
import PayQrDanmaku from '@/components/PayQrDanmaku.vue'
import { refreshProfileFromServer, getAuthHeader, isLoggedIn, redirectToLogin } from '@/utils/auth'

export default {
  name: 'RechargeDialog',
  components: {
    PayQrDanmaku
  },
  data() {
    return {
      rechargePresets: [100, 500, 1000, 2000],
      rechargeDialogVisible: false,
      rechargeAmount: 100,
      rechargePayType: 'alipay',
      rechargePayMethod: 'web',
      rechargePayLoading: false,
      rechargeQrVisible: false,
      rechargeRawVisible: false,
      rechargeQrDataUrl: '',
      rechargeRawPayInfo: '',
      lastRechargeOutTradeNo: '',
      rechargeResultVisible: false,
      rechargeResultLoading: false,
      rechargeResultError: '',
      rechargeResultOrder: null,
      rechargeResultTimer: null,
      rechargeResultTries: 0,
      rechargeResultOutNo: '',
      giftRules: [],
      cashbackRules: []
    }
  },
  computed: {
    matchedGiftTip() {
      const amount = Number(this.rechargeAmount)
      if (!Number.isFinite(amount) || !this.giftRules.length) return ''
      const matched = this.giftRules
        .filter((r) => amount + 1e-9 >= Number(r.min_amount))
        .sort((a, b) => Number(b.min_amount) - Number(a.min_amount))[0]
      if (!matched) return ''
      return this.$t('rechargeGift.tip', {
        amount: Number(matched.min_amount).toFixed(0),
        coupon: matched.coupon_name || this.$t('coupons.unnamed'),
        discount: Number(matched.discount_value).toFixed(2),
        count: matched.grant_count || 1
      })
    },
    matchedCashbackTip() {
      const amount = Number(this.rechargeAmount)
      if (!Number.isFinite(amount) || !this.cashbackRules.length) return ''
      const matched = this.cashbackRules
        .filter((r) => amount + 1e-9 >= Number(r.min_amount))
        .sort((a, b) => Number(b.min_amount) - Number(a.min_amount))[0]
      if (!matched) return ''
      return this.$t('rechargeCashback.tip', {
        amount: Number(matched.min_amount).toFixed(0),
        cashback: Number(matched.cashback_amount).toFixed(2)
      })
    }
  },
  watch: {
    $route: {
      handler(to) {
        const out = String(to.query.recharge_out || to.query.out_trade_no || '').trim()
        if (!out) return
        if (to.path !== '/home' && to.path !== '/') return
        const keep = out
        this.stripRechargeOutQuery()
        this.openRechargeResultDialog(keep)
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.clearRechargeResultTimer()
  },
  methods: {
    open() {
      refreshProfileFromServer()
      this.rechargeDialogVisible = true
      this.loadGiftRules()
      this.loadCashbackRules()
    },
    async loadGiftRules() {
      if (process.env.VUE_APP_USE_BACKEND !== 'true') return
      try {
        const res = await fetch(this.apiUrl('/api/marketing/recharge-gifts'))
        const data = await res.json().catch(() => ({}))
        if (data.ok && Array.isArray(data.list)) this.giftRules = data.list
      } catch (e) {
        /* ignore */
      }
    },
    async loadCashbackRules() {
      if (process.env.VUE_APP_USE_BACKEND !== 'true') return
      try {
        const res = await fetch(this.apiUrl('/api/marketing/recharge-cashbacks'))
        const data = await res.json().catch(() => ({}))
        if (data.ok && Array.isArray(data.list)) this.cashbackRules = data.list
      } catch (e) {
        /* ignore */
      }
    },
    apiUrl(path) {
      const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
      const p = path.startsWith('/') ? path : `/${path}`
      return root ? `${root}${p}` : p
    },
    onRechargeDialogClosed() {
      this.rechargePayLoading = false
    },
    stripRechargeOutQuery() {
      const q = { ...this.$route.query }
      if (!q.recharge_out && !q.out_trade_no) return
      delete q.recharge_out
      delete q.out_trade_no
      this.$router.replace({ path: this.$route.path, query: q })
    },
    clearRechargeResultTimer() {
      if (this.rechargeResultTimer) {
        clearInterval(this.rechargeResultTimer)
        this.rechargeResultTimer = null
      }
    },
    onRechargeResultClosed() {
      this.clearRechargeResultTimer()
      this.rechargeResultOrder = null
      this.rechargeResultError = ''
      this.rechargeResultOutNo = ''
    },
    onRechargeResultCloseBtn() {
      this.rechargeResultVisible = false
      if (this.$route.path !== '/home') {
        this.$router.push('/home')
      }
    },
    openRechargeResultDialog(outTradeNo) {
      this.rechargeResultOutNo = outTradeNo
      this.rechargeResultVisible = true
      this.rechargeResultLoading = true
      this.rechargeResultError = ''
      this.rechargeResultOrder = null
      this.rechargeResultTries = 0
      this.clearRechargeResultTimer()
      this.pollRechargeResultOnce(false)
      this.rechargeResultTimer = setInterval(() => {
        this.rechargeResultTries += 1
        if (this.rechargeResultTries > 40) {
          this.clearRechargeResultTimer()
          return
        }
        if (this.rechargeResultOrder && this.rechargeResultOrder.status === 'paid') {
          this.clearRechargeResultTimer()
          return
        }
        this.pollRechargeResultOnce(true)
      }, 2500)
    },
    rechargeFormatMoney(n) {
      const x = Number(n)
      return new Intl.NumberFormat(this.$i18n.locale === 'en-US' ? 'en-US' : 'zh-CN', {
        style: 'currency',
        currency: 'CNY'
      }).format(Number.isNaN(x) ? 0 : x)
    },
    rechargeStatusType(s) {
      if (s === 'paid') return 'success'
      if (s === 'failed') return 'danger'
      return 'info'
    },
    rechargeStatusText(s) {
      const map = {
        pending: this.$t('recharge.stPending'),
        paid: this.$t('recharge.stPaid'),
        failed: this.$t('recharge.stFailed'),
        closed: this.$t('recharge.stClosed')
      }
      return map[s] || s
    },
    async pollRechargeResultOnce(silent) {
      const out = String(this.rechargeResultOutNo || '').trim()
      if (!out) {
        this.rechargeResultError = this.$t('recharge.missingOrder')
        this.rechargeResultLoading = false
        return
      }
      const headers = { ...getAuthHeader() }
      if (!headers.Authorization) {
        this.rechargeResultError = this.$t('recharge.needLogin')
        this.rechargeResultLoading = false
        return
      }
      if (!silent) this.rechargeResultLoading = true
      try {
        const res = await fetch(
          `${this.apiUrl('/api/recharge/status')}?out_trade_no=${encodeURIComponent(out)}`,
          { headers }
        )
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          this.rechargeResultError = data.message || this.$t('recharge.queryFailed')
          this.rechargeResultOrder = null
          return
        }
        this.rechargeResultOrder = data.order
        this.rechargeResultError = ''
        if (data.order && data.order.status === 'paid') {
          await refreshProfileFromServer()
          this.clearRechargeResultTimer()
        }
      } catch (e) {
        this.rechargeResultError = this.$t('recharge.network')
      } finally {
        if (!silent) this.rechargeResultLoading = false
      }
    },
    onRechargeQrDone() {
      this.rechargeQrVisible = false
      if (this.lastRechargeOutTradeNo) {
        this.openRechargeResultDialog(this.lastRechargeOutTradeNo)
      }
    },
    async onRechargePay() {
      if (!isLoggedIn()) {
        this.$message.error(this.$t('recharge.needLogin'))
        this.rechargeDialogVisible = false
        redirectToLogin(this.$router, this.$route.fullPath)
        return
      }
      const headers = {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      }
      this.rechargePayLoading = true
      this.rechargeQrVisible = false
      this.rechargeRawPayInfo = ''
      try {
        const res = await fetch(this.apiUrl('/api/recharge/create'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            amount: this.rechargeAmount,
            type: this.rechargePayType,
            method: this.rechargePayMethod
          })
        })
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          if (data.message === 'PAY_PUBLIC_API_BASE_NOT_SET' || data.message === 'PAY_RETURN_FRONT_BASE_NOT_SET') {
            this.$message.error(this.$t('recharge.configMissing'))
          } else if (res.status === 401) {
            this.$message.error(this.$t('recharge.needLogin'))
            this.rechargeDialogVisible = false
            redirectToLogin(this.$router, this.$route.fullPath)
          } else {
            this.$message.error(data.detail || data.message || this.$t('recharge.failed'))
          }
          return
        }
        this.lastRechargeOutTradeNo = data.out_trade_no
        const payType = String(data.pay_type || '')
        const payInfo = String(data.pay_info || '')
        if (payType === 'jump') {
          window.location.href = payInfo
          return
        }
        if (payType === 'qrcode') {
          this.rechargeQrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
            payInfo
          )}`
          this.rechargeDialogVisible = false
          this.rechargeQrVisible = true
          return
        }
        if (payType === 'html') {
          const blob = new Blob([payInfo], { type: 'text/html;charset=utf-8' })
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank')
          this.rechargeDialogVisible = false
          this.openRechargeResultDialog(this.lastRechargeOutTradeNo)
          return
        }
        if (payType === 'urlscheme') {
          window.location.href = payInfo
          return
        }
        this.rechargeRawPayInfo = payInfo || JSON.stringify(data, null, 2)
        this.rechargeRawVisible = true
      } finally {
        this.rechargePayLoading = false
      }
    }
  }
}
</script>

<style scoped>

.recharge-float__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.recharge-float__presets :deep(.el-button) {
  min-width: 68px;
  margin: 0;
}

.recharge-float__amount {
  width: 100%;
}

.recharge-float__gift {
  margin: 8px 0 0;
  color: #c2410c;
  font-size: 13px;
  line-height: 1.4;
}
.recharge-float__amount :deep(.el-input__inner) {
  text-align: left;
}

.recharge-float__full {
  width: 100%;
}

.recharge-float__submit {
  width: 100%;
  margin-top: 6px;
}

.recharge-float__qr {
  text-align: center;
}

.recharge-float__qrImg {
  width: 220px;
  height: 220px;
}

.recharge-float__qrTip {
  margin-top: 12px;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.62);
}

.recharge-float__raw {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  max-height: 360px;
  overflow: auto;
}

.recharge-float__state {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(15, 23, 42, 0.65);
}

.recharge-float__state--err {
  color: #b91c1c;
}

.recharge-float__detail {
  margin-top: 8px;
}

.recharge-float__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: rgba(15, 23, 42, 0.55);
}

.recharge-float__row code {
  font-size: 12px;
  color: #0f172a;
  word-break: break-all;
  text-align: right;
}
</style>

<style>
/* append-to-body：挂载到 body，需非 scoped；仅作用于带 recharge-dialog-vc 的弹窗 */
body .el-dialog__wrapper .recharge-dialog-vc.el-dialog {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0 !important;
  max-height: min(90vh, 900px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #dce7e9;
  border-radius: 18px;
  box-shadow: 0 28px 80px rgba(16, 42, 50, 0.18);
}

body .el-dialog__wrapper .recharge-dialog-vc .el-dialog__header {
  flex-shrink: 0;
  border-bottom: 1px solid #e7eef0;
}

body .el-dialog__wrapper .recharge-dialog-vc .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

body .el-dialog__wrapper .recharge-dialog-vc .el-dialog__footer {
  flex-shrink: 0;
}

body .recharge-dialog-vc .el-form-item__label {
  color: #3c5961;
  font-size: 12px;
  font-weight: 650;
}

@media (max-width: 520px) {
  body .el-dialog__wrapper .recharge-dialog-vc.el-dialog {
    width: calc(100% - 28px) !important;
  }
}
</style>
